import { useCallback, useEffect, useState } from 'react'

import { CreatorPermission, DetailedPermission } from '@api'

import CheckIcon from '@/assets/icons/icon-check.svg'
import ExpandMoreIcon from '@/assets/icons/icon-expand-more.svg'
import { Button } from '@/components/ui/button.tsx'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command.tsx'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover.tsx'
import { Tooltip } from '@/components/ui/tooltip'
import { classNames } from '@/utils/helpers.ts'

interface Props {
  ofPermissions: DetailedPermission[]
  disabledPermissions?: Record<string, string | boolean>
  selectedPermissions: Record<string, boolean>
  index: number
  onChangeOfPermission: (
    index: number,
    permission: CreatorPermission,
    value: boolean,
  ) => void
  disabled: boolean
}

const ALL_PERMISSION_SELECTED_WARNING = 'All permissions selected'

export const OfPermissionsCombobox = ({
  ofPermissions,
  selectedPermissions,
  disabledPermissions,
  onChangeOfPermission,
  index,
  disabled,
}: Props) => {
  const [open, setOpen] = useState(false)
  const [permissionsObject, setPermissionsObject] =
    useState<Record<string, boolean>>(selectedPermissions)
  const currentPermissions = ofPermissions.filter(
    (permission) => permission.value && permissionsObject[permission.value],
  )
  const allPermissionsSelected =
    permissionsObject[CreatorPermission.OF_ADMIN] ||
    currentPermissions.length >= ofPermissions.length - 1

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        ofPermissions.forEach((permission) => {
          if (permission.value) {
            if (permissionsObject[permission.value]) {
              onChangeOfPermission(
                index,
                permission.value as CreatorPermission,
                true,
              )
            } else {
              onChangeOfPermission(
                index,
                permission.value as CreatorPermission,
                false,
              )
            }
          }
        })
      }
      setOpen(isOpen)
    },
    [index, ofPermissions, onChangeOfPermission, permissionsObject],
  )

  useEffect(() => {
    setPermissionsObject(selectedPermissions)
  }, [selectedPermissions])

  const handleChange = useCallback((permissionValue: string) => {
    setPermissionsObject((old) => {
      return {
        ...old,
        [permissionValue]: !old[permissionValue],
      }
    })
  }, [])

  return (
    <Popover
      open={open}
      onOpenChange={handleOpenChange}
    >
      <PopoverTrigger asChild>
        <Button
          disabled={disabled}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="flex justify-between"
        >
          <div className="flex gap-2 font-normal">
            {currentPermissions.length > 0 && !disabled ? (
              <span>
                {!allPermissionsSelected ? (
                  <>
                    {currentPermissions.length -
                      (permissionsObject['OF_ADMIN'] ? 1 : 0)}{' '}
                    selected
                  </>
                ) : (
                  <>All permissions</>
                )}
              </span>
            ) : (
              <span>Only chatting</span>
            )}
          </div>
          <ExpandMoreIcon className="w-5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0">
        <Command>
          <CommandList>
            <CommandInput placeholder="Search permission" />
            <CommandEmpty>No Permissions found.</CommandEmpty>
            <CommandSeparator />
            <CommandGroup className=" max-h-44 overflow-auto">
              {ofPermissions.map((permission) => {
                const isDisabled =
                  (permissionsObject[CreatorPermission.OF_ADMIN] &&
                    permission.value !== CreatorPermission.OF_ADMIN) ||
                  !!(
                    permission.value && disabledPermissions?.[permission.value]
                  )
                const isSelected =
                  (permission.value && permissionsObject[permission.value]) ||
                  isDisabled

                return (
                  <Tooltip
                    key={permission.value}
                    content={
                      isDisabled
                        ? (permission.value &&
                            disabledPermissions?.[permission.value]) ||
                          ALL_PERMISSION_SELECTED_WARNING
                        : ''
                    }
                  >
                    <CommandItem
                      key={permission.value}
                      onSelect={() =>
                        permission.value && handleChange(permission.value)
                      }
                      disabled={isDisabled}
                      className={classNames(
                        isDisabled ? 'cursor-not-allowed opacity-50' : '',
                        permission.value === CreatorPermission.OF_ADMIN
                          ? 'border-b-2'
                          : '',
                      )}
                    >
                      <div
                        className={classNames(
                          'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary-500',
                          isSelected
                            ? 'bg-primary-500 text-white'
                            : 'opacity-50',
                        )}
                      >
                        {isSelected && <CheckIcon className="w-3" />}
                      </div>
                      {permission.displayName}
                    </CommandItem>
                  </Tooltip>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
